import { useEffect, useState } from 'preact/hooks';
import { createApi } from '../api.js';

/**
 * "Begär åtkomst" (owner 2026-10-28): someone the Hub does not know asks for access – staff and freelancers on the
 * sign-in page, clients in the portal – confirms the address with a code by email, and waits for a person to decide.
 * The page answers the same whoever asks.
 */

type Lang = 'sv' | 'en';
type Kind = 'staff' | 'client';

const T = {
  en: {
    title: 'Ask for access',
    introAny: 'Tell us who you are. We email you a code to confirm the address, then someone at KFF decides.',
    who: 'Who are you?',
    choose: 'Choose…',
    whoClient: 'A client of Klara Färdiga Film',
    whoStaff: 'On the KFF team or a freelancer',
    introStaff: 'Tell us who you are. We email you a code to confirm the address, then an admin at KFF decides.',
    introClient: 'Tell us who you are. We email you a code to confirm the address, then your contact at Klara Färdiga Film ties you to your company.',
    name: 'Your name',
    email: 'Email',
    company: 'Company',
    role: 'What do you do?',
    roles: { editor: 'Editing', camera: 'Camera', producer: 'Producer', other: 'Something else' } as Record<string, string>,
    message: 'Message (optional)',
    send: 'Send code',
    codeSent: (e: string) => `If ${e} can ask for access, we've emailed a six-digit code. It works for 15 minutes.`,
    code: 'Code',
    confirm: 'Send the request',
    doneTitle: 'Thanks, your request is sent',
    doneStaff: 'An admin at KFF will look at it. You get an email when you can sign in.',
    doneClient: 'Your contact at Klara Färdiga Film will look at it. You get an email with your way into the portal.',
    back: 'Back to sign-in',
    wait: 'Wait…',
    off: 'Asking for access is not possible here. Ask your contact at Klara Färdiga Film.'
  },
  sv: {
    title: 'Begär åtkomst',
    introAny: 'Berätta vem du är. Vi mejlar en kod som bekräftar adressen, sedan avgör någon på KFF.',
    who: 'Vem är du?',
    choose: 'Välj…',
    whoClient: 'Kund hos Klara Färdiga Film',
    whoStaff: 'I KFF-teamet eller frilansare',
    introStaff: 'Berätta vem du är. Vi mejlar en kod som bekräftar adressen, sedan avgör en admin på KFF.',
    introClient: 'Berätta vem du är. Vi mejlar en kod som bekräftar adressen, sedan kopplar din kontakt på Klara Färdiga Film dig till ditt företag.',
    name: 'Ditt namn',
    email: 'E-post',
    company: 'Företag',
    role: 'Vad jobbar du med?',
    roles: { editor: 'Redigering', camera: 'Kamera', producer: 'Producent', other: 'Annat' } as Record<string, string>,
    message: 'Meddelande (valfritt)',
    send: 'Skicka kod',
    codeSent: (e: string) => `Om ${e} kan begära åtkomst har vi mejlat en sexsiffrig kod. Den gäller i 15 minuter.`,
    code: 'Kod',
    confirm: 'Skicka förfrågan',
    doneTitle: 'Tack, din förfrågan är skickad',
    doneStaff: 'En admin på KFF tittar på den. Du får ett mejl när du kan logga in.',
    doneClient: 'Din kontakt på Klara Färdiga Film tittar på den. Du får ett mejl med din väg in i portalen.',
    back: 'Tillbaka till inloggningen',
    wait: 'Vänta…',
    off: 'Det går inte att begära åtkomst här. Fråga din kontakt på Klara Färdiga Film.'
  }
};

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Whether the Hub can send the code here (it needs a mail sender); null while asking. */
export function useAccessEnabled(ask = true): boolean | null {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    if (!ask || on !== null) return;
    createApi(null)
      .get<{ enabled: boolean }>('/api/access/status')
      .then(
        (r) => setOn(r.enabled),
        () => setOn(false)
      );
  }, [ask]);
  return on;
}

/** `kind` fixed by the page, or left out on the one sign-in page for everyone: the person says who they are first. */
export function AccessRequestForm(p: { kind?: Kind; lang: Lang; onBack: () => void }) {
  const t = T[p.lang];
  const [kind, setKind] = useState<Kind | ''>(p.kind ?? '');
  const [step, setStep] = useState<'form' | 'code' | 'done'>('form');
  const [f, setF] = useState({ name: '', email: '', company: '', role: 'editor', message: '' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: Event) => setF({ ...f, [k]: (e.target as HTMLInputElement).value });
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const send = () =>
    run(async () => {
      await createApi(null).post('/api/access/request', { kind, lang: p.lang, name: f.name, email: f.email.trim(), ...(kind === 'client' ? { company: f.company } : { role: f.role }), message: f.message });
      setStep('code');
    });
  const confirm = () =>
    run(async () => {
      await createApi(null).post('/api/access/verify', { email: f.email.trim(), code: code.replace(/\s/g, ''), lang: p.lang });
      setStep('done');
    });
  const err = error ? (
    <p class="field-error" role="alert">
      {error}
    </p>
  ) : null;
  if (step === 'done')
    return (
      <div class="access-form" role="status">
        <h2 class="access-title">{t.doneTitle}</h2>
        <p>{kind === 'client' ? t.doneClient : t.doneStaff}</p>
        <button type="button" class="btn" onClick={p.onBack}>
          {t.back}
        </button>
      </div>
    );
  if (step === 'code')
    return (
      <form
        class="access-form"
        onSubmit={(e) => {
          e.preventDefault();
          void confirm();
        }}
      >
        <h2 class="access-title">{t.title}</h2>
        <p role="status">{t.codeSent(f.email.trim())}</p>
        <div class="field">
          <label class="label" for="ar-code">
            {t.code}
          </label>
          <input class="input" id="ar-code" inputMode="numeric" autocomplete="one-time-code" maxLength={7} required value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} />
        </div>
        {err}
        <button type="submit" class="btn btn-primary" disabled={busy || !/^\d{6}$/.test(code.replace(/\s/g, ''))}>
          {busy ? t.wait : t.confirm}
        </button>
      </form>
    );
  return (
    <form
      class="access-form"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <h2 class="access-title">{t.title}</h2>
      <p>{kind === 'client' ? t.introClient : kind === 'staff' ? t.introStaff : t.introAny}</p>
      {p.kind ? null : (
        <div class="field">
          <label class="label" for="ar-kind">
            {t.who}
          </label>
          <select class="input input--wide" id="ar-kind" required value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value as Kind)}>
            <option value="" disabled>
              {t.choose}
            </option>
            <option value="client">{t.whoClient}</option>
            <option value="staff">{t.whoStaff}</option>
          </select>
        </div>
      )}
      <div class="field">
        <label class="label" for="ar-name">
          {t.name}
        </label>
        <input class="input input--wide" id="ar-name" autocomplete="name" required value={f.name} onInput={set('name')} />
      </div>
      <div class="field">
        <label class="label" for="ar-email">
          {t.email}
        </label>
        <input class="input input--wide" id="ar-email" type="email" autocomplete="email" required value={f.email} onInput={set('email')} />
      </div>
      {kind === 'client' ? (
        <div class="field">
          <label class="label" for="ar-company">
            {t.company}
          </label>
          <input class="input input--wide" id="ar-company" autocomplete="organization" required value={f.company} onInput={set('company')} />
        </div>
      ) : kind === 'staff' ? (
        <div class="field">
          <label class="label" for="ar-role">
            {t.role}
          </label>
          <select class="input input--wide" id="ar-role" value={f.role} onChange={set('role')}>
            {Object.entries(t.roles).map(([k, v]) => (
              <option value={k}>{v}</option>
            ))}
          </select>
        </div>
      ) : null}
      <div class="field">
        <label class="label" for="ar-msg">
          {t.message}
        </label>
        <textarea class="input input--wide" id="ar-msg" rows={2} maxLength={1000} value={f.message} onInput={set('message')} />
      </div>
      {err}
      <span class="cluster">
        <button type="submit" class="btn btn-primary" disabled={busy || !kind || !f.name.trim() || !f.email.trim() || (kind === 'client' && !f.company.trim())}>
          {busy ? t.wait : t.send}
        </button>
        <button type="button" class="btn btn-ghost" onClick={p.onBack}>
          {t.back}
        </button>
      </span>
    </form>
  );
}
